import React from 'react'
import PropTypes from 'prop-types'

import { agreementDisplayStatusOf, agreementPresentation } from './agreementStatus'

/** Styles by enrollment_status (and legacy member-status labels). */
const STATUS_STYLES = {
  in_progress: {
    dot: 'bg-warning/500',
    pill:
      'border border-warning/200 bg-warning/50 text-warning/800 ring-1 ring-inset ring-warning/100',
  },
  active: {
    dot: 'bg-success/500',
    pill:
      'border border-success/200 bg-success/50 text-success/800 ring-1 ring-inset ring-success/100',
  },
  completed: {
    dot: 'bg-success/500',
    pill:
      'border border-success/200 bg-success/50 text-success/800 ring-1 ring-inset ring-success/100',
  },
  expired: {
    dot: 'bg-gray-light/400',
    pill:
      'border border-gray-light/200 bg-gray-light/50 text-gray-light/800 ring-1 ring-inset ring-gray-light/100',
  },
  rejected: {
    dot: 'bg-error/500',
    pill: 'border border-error/200 bg-error/50 text-error/800 ring-1 ring-inset ring-error/100',
  },
  // Membership (verification_status) states — shown once a company is reviewed.
  approved: {
    dot: 'bg-success/500',
    pill:
      'border border-success/200 bg-success/50 text-success/800 ring-1 ring-inset ring-success/100',
  },
  waiting_approval: {
    dot: 'bg-warning/500',
    pill:
      'border border-warning/200 bg-warning/50 text-warning/800 ring-1 ring-inset ring-warning/100',
  },
  'Waiting CLIK approval': {
    dot: 'bg-warning/500',
    pill:
      'border border-warning/200 bg-warning/50 text-warning/800 ring-1 ring-inset ring-warning/100',
  },
  'Document submission': {
    dot: 'bg-gray-light/400',
    pill:
      'border border-gray-light/200 bg-gray-light/50 text-gray-light/800 ring-1 ring-inset ring-gray-light/100',
  },
  Active: {
    dot: 'bg-success/500',
    pill:
      'border border-success/200 bg-success/50 text-success/800 ring-1 ring-inset ring-success/100',
  },
  Rejected: {
    dot: 'bg-error/500',
    pill: 'border border-error/200 bg-error/50 text-error/800 ring-1 ring-inset ring-error/100',
  },
}

const FALLBACK = {
  dot: 'bg-gray-light/400',
  pill: 'border border-gray-light/200 bg-gray-light/50 text-gray-light/700',
}

function normalizeKey(status) {
  if (status == null || status === '') return ''
  return String(status)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
}

/**
 * Derive the company's "Member status" for display/filtering. The membership
 * lifecycle (verification_status) takes precedence once a company is reviewed;
 * before that we fall back to the onboarding phase (enrollment_status).
 * Returns one of: active | expired | approved | waiting_approval | rejected |
 * <enrollment_status> (in_progress | completed | draft | pending) | ''.
 */
export function memberStatusOf(row) {
  const v = row?.verification_status
  if (v === 'active') {
    const exp = row?.agreement_expires_at
    return exp && new Date(exp).getTime() < Date.now() ? 'expired' : 'active'
  }
  if (v === 'approved') return 'approved'
  if (v === 'rejected') return 'rejected'
  if (v === 'pending') return 'waiting_approval'
  return row?.enrollment_status || ''
}

// A company can be enquired against (single, bulk) or given a new employee only
// once its membership is active. Non-active companies still appear in those
// entity pickers, disabled with their status chip, so the user understands why
// they can't pick it instead of the option silently missing.
export const isCompanySelectable = (company) =>
  company?.verification_status !== undefined
    ? memberStatusOf(company) === 'active'
    : !company?.enrollment_status || company.enrollment_status === 'active'

/**
 * `{ label, value }` option of an entity picker from an /option/my-company-list
 * item, keeping the membership fields isCompanySelectable and
 * CompanyPickerOption read.
 */
export const toCompanyOption = (c) => ({
  label: c.name,
  value: c.id,
  enrollment_status: c.enrollment_status,
  verification_status: c.verification_status,
  agreement_expires_at: c.agreement_expires_at,
})

export function formatEnrollmentStatusLabel(status) {
  if (status == null || status === '') return '—'
  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function StatusPill({ label, styles }) {
  return (
    <span
      className={`inline-flex w-fit max-w-full shrink-0 self-start items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${styles.pill}`}
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${styles.dot}`} aria-hidden />
      <span className="whitespace-nowrap">{label}</span>
    </span>
  )
}

/**
 * Enrollment / member status pill (backend: enrollment_status).
 */
export default function MyMemberStatusChip({ status }) {
  const key = normalizeKey(status)
  const styles = STATUS_STYLES[key] ?? STATUS_STYLES[status] ?? FALLBACK
  return <StatusPill label={formatEnrollmentStatusLabel(status)} styles={styles} />
}

// Agreement tones (agreementStatus.js) painted with the member-status pills.
const AGREEMENT_TONE_STYLES = {
  warning: STATUS_STYLES.waiting_approval,
  success: STATUS_STYLES.approved,
  error: STATUS_STYLES.rejected,
  gray: STATUS_STYLES.expired,
}

/**
 * Membership agreement (DocuSign) pill — shown NEXT TO the member status chip,
 * never instead of it: the two are separate axes (an "Approved" member can
 * still be "Waiting CLIK signature"). Pass `status` (an agreement_status value,
 * shown as is) or `company` (any payload carrying agreement_status /
 * agreement.status — shown as what the company waits for, e.g. "Waiting client
 * signature" for a company that must sign a reset agreement again).
 * Renders nothing when no agreement was sent yet.
 */
export function AgreementStatusChip({ status, company }) {
  const presentation = agreementPresentation(status ?? agreementDisplayStatusOf(company))
  if (!presentation) return null
  return (
    <StatusPill
      label={presentation.label}
      styles={AGREEMENT_TONE_STYLES[presentation.tone] ?? FALLBACK}
    />
  )
}

AgreementStatusChip.propTypes = {
  status: PropTypes.string,
  company: PropTypes.object,
}

AgreementStatusChip.defaultProps = {
  status: null,
  company: null,
}

/**
 * `renderOption` body of an entity picker: the company name, plus its member
 * status chip when it can't be picked (pair with
 * `getOptionDisabled={(o) => !isCompanySelectable(o)}`).
 */
export function CompanyPickerOption({ company, label }) {
  const selectable = isCompanySelectable(company)
  return (
    <div className="flex w-full items-center justify-between gap-2">
      <span
        className={`text-md-regular truncate ${selectable ? 'text-gray-light/900' : 'text-gray-light/400'}`}
      >
        {label}
      </span>
      {!selectable && <MyMemberStatusChip status={memberStatusOf(company)} />}
    </div>
  )
}

CompanyPickerOption.propTypes = {
  company: PropTypes.shape({
    verification_status: PropTypes.string,
    enrollment_status: PropTypes.string,
    agreement_expires_at: PropTypes.string,
  }),
  label: PropTypes.string,
}

CompanyPickerOption.defaultProps = {
  company: null,
  label: '',
}

StatusPill.propTypes = {
  label: PropTypes.string.isRequired,
  styles: PropTypes.shape({ dot: PropTypes.string, pill: PropTypes.string }).isRequired,
}

MyMemberStatusChip.propTypes = {
  status: PropTypes.string,
}

MyMemberStatusChip.defaultProps = {
  status: '',
}
